import { useState, useEffect, useCallback } from "react";
import { Gift, Copy, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { trackLeadCapture, trackGenericEvent } from "@/lib/crm/track";
import { isValidBrazilianPhone } from "@/lib/phoneValidation";
import { toast } from "sonner";

const COUPON_CODE = "PRIMEIRA10";
const STORAGE_KEY = "nathof_coupon_popup_dismissed";
const SHOW_DELAY_MS = 30000;

const alreadyDismissed = () =>
  typeof window !== "undefined" &&
  (localStorage.getItem(STORAGE_KEY) === "1" ||
    !!localStorage.getItem("nathof_lead_email"));

/**
 * Popup mobile-first de captura do cupom de 1ª compra (10% — PRIMEIRA10).
 * A comunicação reforça o desconto de boas-vindas (10%) na primeira compra.
 * Aparece por intenção de saída OU após 30s (nunca no load).
 * Fácil de fechar e não reaparece
 * na mesma sessão depois de fechado (localStorage).
 */
export const FirstPurchaseCouponPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);


  const trigger = useCallback(() => {
    if (alreadyDismissed()) return;
    setOpen(true);
    trackGenericEvent("view_coupon_popup");
  }, []);

  useEffect(() => {
    if (alreadyDismissed()) return;

    let fired = false;
    const fireOnce = () => {
      if (fired) return;
      fired = true;
      trigger();
    };


    const timer = setTimeout(fireOnce, SHOW_DELAY_MS);

    // Exit-intent (desktop): cursor sai pelo topo da viewport.
    const onMouseOut = (e: MouseEvent) => {
      if (e.clientY <= 0) fireOnce();
    };

    document.addEventListener("mouseout", onMouseOut);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [trigger]);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch { /* ignore */ }
    setOpen(false);
  };

  const copyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      toast.success("Cupom copiado!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar. Anote: " + COUPON_CODE);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      toast.error("Digite um e-mail válido.");
      return;
    }
    const trimmedWa = whatsapp.trim();
    if (trimmedWa && !isValidBrazilianPhone(trimmedWa)) {
      toast.error("Digite um WhatsApp válido ou deixe em branco.");
      return;
    }

    setSubmitting(true);
    try {
      const derivedName = trimmedEmail.split("@")[0] || "Lead Cupom";
      // anon não tem SELECT policy → upsert(on_conflict) falha com 42501; usar INSERT.
      // NOTE: a policy de INSERT anon só aceita source IN ('consulta_guiada','quiz');
      // 'popup' será rejeitado até que a policy inclua essa origem.
      const { error: insertError } = await supabase.from("quiz_leads").insert({
        name: derivedName,
        email: trimmedEmail,
        whatsapp: trimmedWa,
        source: "popup",
      });
      if (insertError && insertError.code !== "23505") {
        // eslint-disable-next-line no-console
        console.error("[QuizLead] insert failed", insertError);
      }

      localStorage.setItem("nathof_lead_email", trimmedEmail);
      localStorage.setItem(STORAGE_KEY, "1");

      trackLeadCapture(
        "popup",
        { email: trimmedEmail, phone: trimmedWa || undefined, name: derivedName },
        { emailOptIn: true, whatsappOptIn: !!trimmedWa },
      );

      setSuccess(true);
    } catch {
      toast.error("Não foi possível salvar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] flex justify-center p-3 pointer-events-none sm:inset-0 sm:items-center sm:p-4 sm:bg-foreground/40 sm:backdrop-blur-sm sm:pointer-events-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Cupom de primeira compra"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md sm:max-w-sm bg-[#F9F7F5] rounded-2xl shadow-2xl p-4 sm:p-6 pointer-events-auto animate-in slide-in-from-bottom-4 sm:fade-in duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          aria-label="Fechar"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/5"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
          <Gift className="w-6 h-6 text-primary" />
        </div>

        {!success ? (
          <>
            <h2 className="text-xl font-narrow font-semibold text-foreground">
              10% de boas-vindas na primeira compra
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5">
              Use o cupom <strong className="text-foreground">PRIMEIRA10</strong> e ganhe 10% na primeira compra.
            </p>

            <form onSubmit={handleSubmit} className="mt-4 space-y-2.5">
              <Input
                type="email"
                inputMode="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                aria-label="Seu e-mail"
                required
              />
              <Input
                type="tel"
                inputMode="tel"
                placeholder="WhatsApp (opcional)"
                value={whatsapp}
                onChange={(ev) => setWhatsapp(ev.target.value)}
                aria-label="Seu WhatsApp (opcional)"
              />

              <Button
                type="submit"
                disabled={submitting}
                className="w-full min-h-[48px] text-base font-semibold"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Quero meu cupom de 10%
              </Button>
            </form>

            <button
              onClick={dismiss}
              className="block mx-auto mt-3 text-xs text-muted-foreground underline underline-offset-2"
            >
              Agora não
            </button>
          </>
        ) : (
          <>
            <h2 className="text-xl font-narrow font-semibold text-foreground">
              Seu cupom está pronto 🌿
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5">
              Use o código abaixo no checkout para ganhar 10% na sua 1ª compra.
            </p>

            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1 text-center font-semibold tracking-wider text-foreground bg-background border border-dashed border-primary/40 rounded-xl py-3">
                {COUPON_CODE}
              </div>
              <Button
                type="button"
                onClick={copyCoupon}
                variant="outline"
                className="min-h-[48px] px-3"
                aria-label="Copiar cupom"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>

            <Button
              onClick={dismiss}
              asChild
              className="w-full mt-3 min-h-[48px] text-base font-semibold"
            >
              <a href="/loja">Comprar com 10%</a>
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default FirstPurchaseCouponPopup;
